import { canCreateUsers } from '@/auth/roles.js';
import { useSession } from '@/auth/useSession.js';
import { InfoPage } from '@/components/info-page.js';
import { StudentShortLink } from '@/components/join-now/student-short-link.js';
import { StudentStatusBadge } from '@/components/students/student-status-badge.js';
import { createTableRowSelectionCell, EntityTable } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { getNowPlainDateISO } from '@/lib/time-zones.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { MultiSelectOption } from '@/components/ui/multi-select.js';
import { buildNameLikeSelectOptions } from '@/components/ui/multi-select-utils.js';
import {
  lessonTypeaheadSearch,
  useAllLearningPaths,
  useClassTimeOptions,
  useLessonById,
} from '@/lib/api-call-hooks.js';
import { EmptyArray, getSchool, getStudentsOfSchool } from '@/lib/api-calls.js';
import { buildBooleanSelectOptions } from '@/components/ui/multi-select-utils.js';
import { useRowsBulkSelection } from '@/lib/bulk-selection.js';
import { exportToCSV } from '@/lib/csv-utils.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { booleanFilterToSelectedValues, selectedValuesToBooleanFilter, usePaginatedFilters } from '@/lib/filtering.js';
import { DEFAULT_PAGE_SIZE, pageSizeSchemaWithMax, paginationRequestSchema } from '@/lib/pagination.js';
import { generateQrCodesPdf } from '@/lib/qr-code-pdf-utils.js';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing.js';
import { useSorting } from '@/lib/sorting.js';
import { getStudentStatusOptions } from '@/lib/student-utils.js';
import { airColumnHelper } from '@/lib/tables.js';
import { BulkAssignClassTimes } from '@/routes/_layout/orgs/schools/-components/bulk-assign-class-times.js';
import { BulkAssignStartingLessons } from '@/routes/_layout/orgs/schools/-components/bulk-assign-starting-lessons.js';
import { BulkResetStartingLessons } from '@/routes/_layout/orgs/schools/-components/bulk-reset-starting-lessons.js';
import { GenerateMakeupDiagnosticSessions } from '@/routes/_layout/orgs/schools/-components/generate-makeup-diagnostic-sessions.js';
import { GenerateOnboardingSession } from '@/routes/_layout/orgs/schools/-components/generate-onboarding-session.js';
import { GenerateRegularSessions } from '@/routes/_layout/orgs/schools/-components/generate-regular-sessions.js';
import { NoTimeZoneWarning } from '@/routes/_layout/orgs/schools/-components/no-time-zone-warning.js';
import { StudentSpecialNeedBadges } from '@/routes/_layout/orgs/schools/-components/student-special-need-badges.js';
import { BulkUpdateLearningPathByCsv } from '@/routes/_layout/students/-components/bulk-update-learning-path-by-csv.js';
import {
  formatScheduleDates,
  formatStudentClassTimeAbbr,
} from '@/routes/_layout/scheduling/schedules/-components/formatting.js';
import { CreateStudent } from '@/routes/_layout/users/-components/create-student.js';
import { CreateStudentsByCsv } from '@/routes/_layout/users/-components/create-students-by-csv.js';
import { UpdateEdlinkStudentsByCsv } from '@/routes/_layout/users/-components/update-edlink-students-by-csv.js';
import { FrequentTimeZones, getSessionFormattedSchedule, isAirAdmin, PrismaEnums } from '@airreading/common';
import {
  adjustMinimalClassTimeAcrossTZ,
  GetStudentsOfSchoolSortableColumn,
  getStudentsOfSchoolSortableColumnSchema,
  getStudentsOfSchoolSortOrderSchema,
  LARGE_MAX_PAGE_SIZE,
  StudentOfSchool,
  studentStatusSchema,
  UNASSIGNED_LEARNING_PATH_FILTER_ID,
  StudentYearColorMap,
  studentYearOption,
} from '@airreading/common/data';
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ChevronDown, PencilIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { DialogCoachesAssigned } from '../-components/dialog-coaches-assigned.js';
import { SchoolPageTitle } from '../-components/school-page-title.js';
import { schoolDetailsTabs } from '../-components/tabs.js';
import { EdlinkIdCode } from '@/components/alphanumeric-code.js';
import { BulkAssignReturningStudents } from '../-components/bulk-assign-returning-students.js';

const searchParamsSchema = paginationRequestSchema.extend({
  pageSize: pageSizeSchemaWithMax(LARGE_MAX_PAGE_SIZE, 50),
  grades: z.array(z.nativeEnum(PrismaEnums.StudentYear)).optional(),
  query: z.string().optional(),
  startingLessons: z.array(z.string()).optional(),
  classTimeIds: z.array(z.string()).optional(),
  learningPathIds: z.array(z.string()).optional(),
  statuses: z.array(studentStatusSchema).optional(),
  hasEdlinkId: z.boolean().optional(),
  zeroFutureSessions: z.boolean().optional(),
  sortBy: getStudentsOfSchoolSortOrderSchema.optional(),
});

export const Route = createFileRoute('/_layout/orgs/schools/$schoolId/students')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SchoolStudentsDetails,
  validateSearch: searchParamsSchema,
});

export interface CoachAssignedProps {
  availableCoachesCount: number;
  coachesAssignedCount: number;
  reservedFallbackGroupCount?: number;
}

const columnHelper = airColumnHelper<StudentOfSchool>(getStudentsOfSchoolSortableColumnSchema);
const buildColumns = (timeZone: string | null, showEdlinkIdColumn = false) => [
  createTableRowSelectionCell(columnHelper),

  columnHelper.accessor((row) => row, {
    id: 'id',
    header: '',
    cell: ({ getValue }) => (
      <Link
        to={'/students/$studentId'}
        params={{ studentId: getValue().id }}
        search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
      >
        <PencilIcon size={20} />
      </Link>
    ),
    size: 50,
  }),
  columnHelper.accessor((row) => row, {
    id: 'name',
    header: 'Name',
    cell: ({ getValue }) => {
      const { id, firstName, lastName } = getValue();
      return (
        <Link
          to={'/students/$studentId'}
          params={{ studentId: id }}
          search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
        >
          {firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || 'Unknown'}
        </Link>
      );
    },
  }),
  columnHelper.accessor('shortCode', {
    header: 'Direct join short link',
    id: 'shortLink',
    cell: ({ getValue }) => {
      const shortLink = getValue();
      return <StudentShortLink shortCode={shortLink} />;
    },
  }),
  columnHelper.accessor('grade', {
    header: 'Grade',
    id: 'grade',
    cell: ({ getValue }) => {
      const grade = getValue();
      if (!grade) return studentYearOption(grade);
      const backgroundColor = StudentYearColorMap[grade];
      return (
        <div className="min-w-[80px] flex justify-start">
          <span className="text-xs rounded-full px-2 py-1 min-w-9 text-center" style={{ backgroundColor }}>
            {studentYearOption(grade)}
          </span>
        </div>
      );
    },
  }),
  columnHelper.accessor((row) => row, {
    header: 'Special needs',
    cell: ({ getValue }) => {
      return <StudentSpecialNeedBadges {...getValue()} />;
    },
  }),
  columnHelper.accessor('status', {
    id: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      return <StudentStatusBadge status={getValue()} />;
    },
  }),
  columnHelper.accessor('requestedClassTime', {
    header: 'Schedule',
    id: 'schedule',
    cell: ({ getValue }) => {
      const classTime = getValue();
      if (!classTime) return null;

      return <div>{formatScheduleDates(classTime.schedule)}</div>;
    },
  }),
  columnHelper.accessor('requestedClassTime', {
    header: () => (
      <div className="flex items-center gap-2">Class times {timeZone ? '(CT)' : <NoTimeZoneWarning />}</div>
    ),
    id: 'requestedClassTime',
    cell: ({ getValue }) => {
      const classTime = getValue();
      if (!classTime) return null;

      return (
        <Badge variant="outline">
          {formatStudentClassTimeAbbr(
            timeZone ? adjustMinimalClassTimeAcrossTZ(classTime, timeZone, FrequentTimeZones.UsCentral) : classTime,
          )}
        </Badge>
      );
    },
  }),
  columnHelper.accessor((row) => row, {
    header: 'Learning path',
    id: 'learningPath',
    cell: ({ getValue }) => {
      const { learningPath } = getValue();
      if (!learningPath) return null;
      return `${learningPath.name}`;
    },
  }),
  columnHelper.accessor((row) => row, {
    header: 'Starting lesson',
    id: 'startingLesson',
    cell: ({ getValue }) => {
      const { startingLesson } = getValue();
      if (!startingLesson) return null;
      return `${startingLesson.name}`;
    },
  }),
  columnHelper.accessor('nextLesson', {
    id: 'nextLesson',
    header: 'Next lesson',
    cell: ({ getValue }) => {
      const nextLesson = getValue();
      return <div>{nextLesson?.name}</div>;
    },
  }),
  columnHelper.accessor('endLesson', {
    id: 'endLesson',
    header: 'End lesson',
    cell: ({ getValue }) => {
      const endLesson = getValue();
      return <div>{endLesson?.name}</div>;
    },
  }),
  columnHelper.accessor('nextSession', {
    header: 'Next session date (CT)',
    id: 'nextSession',
    cell: ({ getValue }) => {
      const nextSession = getValue();
      if (!nextSession) return null;
      const { startInstant } = nextSession;
      const { sessionDateFormatted, sessionStartTimeFormatted } = getSessionFormattedSchedule({
        startInstant,
        endInstant: startInstant, // don't care about endInstant here
        displayTimeZone: FrequentTimeZones.UsCentral,
      });
      return (
        <div>
          {sessionDateFormatted} - {sessionStartTimeFormatted}
        </div>
      );
    },
  }),
  columnHelper.accessor('totalFutureSessions', {
    header: 'Total future sessions',
    id: 'totalFutureSessions',
  }),
  ...(showEdlinkIdColumn
    ? [
        columnHelper.accessor('edlinkId', {
          header: 'Edlink ID',
          id: 'edlinkId',
          cell: ({ getValue }) => {
            const edlinkId = getValue();
            return edlinkId ? (
              <div className="flex items-center">
                <EdlinkIdCode code={edlinkId} wrapText={false} />
              </div>
            ) : (
              '-'
            );
          },
        }),
      ]
    : []),
];

function SchoolStudentsDetails() {
  const { schoolId } = Route.useParams();
  const { data: school } = useSuspenseQuery(getSchool(schoolId));
  const { toast } = useToast();
  const { actingUser } = useSession();

  const queryClient = useQueryClient();

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[userNameQuery, activeUserNameQuery], setUserNameQuery],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
      startingLessons: [[selectedStartingLessons, activeSelectedStartingLessons], setSelectedStartingLessons],
      classTimeIds: [[classTimeIds, activeClassTimeIds], setClassTimeIds],
      learningPathIds: [[learningPathIds, activeLearningPathIds], setLearningPathIds],
      statuses: [[selectedStatuses, activeSelectedStatuses], setSelectedStatuses],
      hasEdlinkId: [[hasEdlinkId, activeHasEdlinkId], setHasEdlinkId],
      zeroFutureSessions: [[zeroFutureSessions, activeZeroFutureSessions], setZeroFutureSessions],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, [
    'query',
    'grades',
    'startingLessons',
    'classTimeIds',
    'learningPathIds',
    'statuses',
    'hasEdlinkId',
    'zeroFutureSessions',
  ]);
  const [sortBy, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetStudentsOfSchoolSortableColumn>([sortBy, didChangeSortBy]);

  const [showCoachWarningDialog, setShowCoachWarningDialog] = useState(false);
  const [coachesAssignedCount, setCoachesAssignedCount] = useState<CoachAssignedProps>({
    availableCoachesCount: -1,
    coachesAssignedCount: -1,
    reservedFallbackGroupCount: 0,
  });

  useEffect(() => {
    if (!school.edlinkId && hasEdlinkId !== undefined) {
      setHasEdlinkId(undefined);
    }
  }, [school.edlinkId, hasEdlinkId, setHasEdlinkId]);

  const columns = useMemo(
    () => buildColumns(school.org.timeZone, !!school.edlinkId),
    [school.org.timeZone, school.edlinkId],
  );

  const exportColumns = useMemo(
    () => [
      {
        id: 'Name',
        getValue: (student: StudentOfSchool) => {
          const { firstName, lastName } = student;
          return firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || 'Unknown';
        },
      },
      {
        id: 'Short Link',
        getValue: (student: StudentOfSchool) => {
          const baseUrl = import.meta.env.VITE_JOIN_NOW_URL;
          return student.shortCode ? `${baseUrl}/${student.shortCode}` : '';
        },
      },
    ],
    [],
  );

  const { data, isPending, error, refetch } = useQuery(
    getStudentsOfSchool(schoolId, {
      pageIndex,
      pageSize,
      filters: {
        query: activeUserNameQuery,
        grades: activeSelectedGrades,
        startingLessons: activeSelectedStartingLessons,
        classTimeIds: activeClassTimeIds,
        learningPathIds: activeLearningPathIds,
        statuses: activeSelectedStatuses,
        hasEdlinkId: activeHasEdlinkId,
        zeroFutureSessions: activeZeroFutureSessions,
      },
      sortBy,
    }),
  );
  const { classTimeOptions } = useClassTimeOptions({ schoolId });
  const { learningPaths = EmptyArray } = useAllLearningPaths();
  const learningPathsOptions: MultiSelectOption[] = [
    { label: '-', value: UNASSIGNED_LEARNING_PATH_FILTER_ID },
    ...learningPaths.map(buildNameLikeSelectOptions),
  ];

  const hasActiveFilters = useMemo(() => {
    return !!(
      activeUserNameQuery ||
      (activeSelectedGrades && activeSelectedGrades.length > 0) ||
      (activeSelectedStartingLessons && activeSelectedStartingLessons.length > 0) ||
      (activeClassTimeIds && activeClassTimeIds.length > 0) ||
      (activeLearningPathIds && activeLearningPathIds.length > 0) ||
      (activeSelectedStatuses && activeSelectedStatuses.length > 0) ||
      activeHasEdlinkId !== undefined ||
      activeZeroFutureSessions !== undefined
    );
  }, [
    activeUserNameQuery,
    activeSelectedGrades,
    activeSelectedStartingLessons,
    activeClassTimeIds,
    activeLearningPathIds,
    activeSelectedStatuses,
    activeHasEdlinkId,
    activeZeroFutureSessions,
  ]);

  const students = useMemo(() => data?.students ?? EmptyArray, [data]);
  useErrorToast(error, 'Error loading users. Please try again.');

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<StudentOfSchool>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => students.find((student) => student.id === id),
    });

  const onSuccess = () => {
    resetAllRowSelection();
    refetch();
  };

  const fetchLessonById = useLessonById();

  const topBarRightComponents = canCreateUsers(actingUser) && (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button className="ml-auto hidden h-8 lg:flex">
          Add students
          <ChevronDown className="mr-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[150px]">
        <CreateStudent
          onSuccess={refetch}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Add a student</DropdownMenuItem>}
          schoolId={schoolId}
        />
        <CreateStudentsByCsv
          onSuccess={refetch}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Import from CSV</DropdownMenuItem>}
          schoolId={schoolId}
          from="school"
        />
        <BulkUpdateLearningPathByCsv
          onSuccess={refetch}
          schoolId={schoolId}
          trigger={
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              Bulk update learning path & starting lesson
            </DropdownMenuItem>
          }
        />
        {school.edlinkId && (
          <UpdateEdlinkStudentsByCsv
            onSuccess={refetch}
            trigger={
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>Update Edlink students by CSV</DropdownMenuItem>
            }
            schoolId={schoolId}
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const table = useReactTable({
    data: students,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      rowSelection,
      sorting,
    },
    rowCount: data?.total || 0,
    onRowSelectionChange,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    ...tableSortingOptions,
  });

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search by name, email or state ID"
          value={userNameQuery ?? ''}
          onChange={(event) => {
            setUserNameQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="Grades"
          options={Object.values(PrismaEnums.StudentYear).map((grade) => ({
            label: studentYearOption(grade),
            value: grade,
          }))}
          selectedValues={selectedGrades ?? []}
          onChange={setSelectedGrades}
        />
        <TableFilterDropdown
          title="Starting lessons"
          options={lessonTypeaheadSearch(queryClient)}
          selectedValues={selectedStartingLessons ?? []}
          onChange={setSelectedStartingLessons}
          getOptionSearchFn={fetchLessonById}
        />
        <TableFilterDropdown
          title="Learning paths"
          options={learningPathsOptions}
          selectedValues={learningPathIds ?? []}
          onChange={setLearningPathIds}
        />
        <TableFilterDropdown
          title="Class times (CT)"
          options={classTimeOptions}
          selectedValues={classTimeIds ?? []}
          onChange={setClassTimeIds}
        />
        <TableFilterDropdown
          title="Status"
          options={getStudentStatusOptions()}
          selectedValues={selectedStatuses ?? []}
          onChange={setSelectedStatuses}
        />
        <TableFilterDropdown
          title="0 future session"
          options={buildBooleanSelectOptions()}
          variant="radio"
          selectedValues={booleanFilterToSelectedValues(zeroFutureSessions)}
          onChange={(vals) => setZeroFutureSessions(selectedValuesToBooleanFilter(vals))}
          data-testid="school-students-filter-zero-future-sessions"
        />
        {school.edlinkId && (
          <TableFilterDropdown
            title="Edlink"
            options={buildBooleanSelectOptions()}
            variant="radio"
            selectedValues={booleanFilterToSelectedValues(hasEdlinkId)}
            onChange={(vals) => setHasEdlinkId(selectedValuesToBooleanFilter(vals))}
          />
        )}
        <TableFilterResetButton show={true} onClick={resetFilters} />
      </>
    ),
    [
      classTimeIds,
      classTimeOptions,
      learningPathIds,
      learningPathsOptions,
      queryClient,
      resetFilters,
      selectedGrades,
      selectedStartingLessons,
      setClassTimeIds,
      setLearningPathIds,
      setSelectedGrades,
      setSelectedStartingLessons,
      setUserNameQuery,
      userNameQuery,
      selectedStatuses,
      setSelectedStatuses,
      fetchLessonById,
      hasEdlinkId,
      setHasEdlinkId,
      school.edlinkId,
      zeroFutureSessions,
      setZeroFutureSessions,
    ],
  );
  // Helper function to render the main content based on different states
  const renderStudentsTable = () => {
    // Show table when not loading OR when there are active filters
    if (!isPending || hasActiveFilters) {
      return (
        <EntityTable
          table={table}
          showPagination={true}
          paginationOptions={[10, 20, 30, 40, 50, 100, 150, LARGE_MAX_PAGE_SIZE]}
          isLoading={isPending}
          hasError={!!error}
          emptyState={'No students found'}
          errorState={<TableErrorState refetchAction={refetch} />}
          topBarRightComponents={topBarRightComponents}
          autoManagedColumnIds={['selection', 'id']}
          filters={{ filterLeftComponents }}
          bulkActionsMenuItems={
            <>
              <BulkAssignClassTimes
                students={rowDataSelection}
                schoolId={schoolId}
                onSuccess={onSuccess}
                schoolTimeZone={school.org.timeZone}
                trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Assign class times</DropdownMenuItem>}
              />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Starting lesson</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <BulkAssignStartingLessons
                    students={rowDataSelection}
                    onSuccess={onSuccess}
                    trigger={
                      <DropdownMenuItem onSelect={(e) => e.preventDefault()}>Assign starting lesson</DropdownMenuItem>
                    }
                  />
                  <BulkResetStartingLessons
                    students={rowDataSelection}
                    onSuccess={onSuccess}
                    trigger={
                      <DropdownMenuItem onSelect={(e) => e.preventDefault()}>Reset starting lesson</DropdownMenuItem>
                    }
                  />
                  <BulkAssignReturningStudents
                    students={rowDataSelection}
                    onSuccess={onSuccess}
                    trigger={
                      <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                        Assign returning students
                      </DropdownMenuItem>
                    }
                  />
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Generate classes</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <GenerateOnboardingSession
                    type={school.org.videoConferenceType === 'googleMeet' ? 'practice_google_meet' : 'practice'}
                    orgId={school.org.id}
                    students={rowDataSelection}
                    onSuccess={onSuccess}
                    title="Generate practice classes"
                    trigger={
                      <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                        Generate practice classes
                      </DropdownMenuItem>
                    }
                    onCoachesAssigned={({
                      availableCoachesCount,
                      coachesAssignedCount,
                      reservedFallbackGroupCount,
                    }) => {
                      setCoachesAssignedCount({
                        availableCoachesCount,
                        coachesAssignedCount,
                        reservedFallbackGroupCount,
                      });
                      setShowCoachWarningDialog(true);
                    }}
                    schoolTimeZone={school.org.timeZone}
                  />
                  <GenerateOnboardingSession
                    type={'diagnostic'}
                    orgId={school.org.id}
                    students={rowDataSelection}
                    onSuccess={onSuccess}
                    title="Generate diagnostic classes"
                    trigger={
                      <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                        Generate diagnostic classes
                      </DropdownMenuItem>
                    }
                    onCoachesAssigned={({
                      availableCoachesCount,
                      coachesAssignedCount,
                      reservedFallbackGroupCount,
                    }) => {
                      setCoachesAssignedCount({
                        availableCoachesCount,
                        coachesAssignedCount,
                        reservedFallbackGroupCount,
                      });
                      setShowCoachWarningDialog(true);
                    }}
                    schoolTimeZone={school.org.timeZone}
                  />
                  <GenerateOnboardingSession
                    type={
                      school.org.videoConferenceType === 'googleMeet'
                        ? 'practice_and_diagnostic_google_meet'
                        : 'practice_and_diagnostic'
                    }
                    orgId={school.org.id}
                    students={rowDataSelection}
                    onSuccess={onSuccess}
                    title="Generate practice & diagnostic classes"
                    trigger={
                      <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                        Generate practice & diagnostic classes
                      </DropdownMenuItem>
                    }
                    onCoachesAssigned={({
                      availableCoachesCount,
                      coachesAssignedCount,
                      reservedFallbackGroupCount,
                    }) => {
                      setCoachesAssignedCount({
                        availableCoachesCount,
                        coachesAssignedCount,
                        reservedFallbackGroupCount,
                      });
                      setShowCoachWarningDialog(true);
                    }}
                    schoolTimeZone={school.org.timeZone}
                  />
                  <GenerateMakeupDiagnosticSessions
                    students={rowDataSelection}
                    orgId={school.org.id}
                    onSuccess={onSuccess}
                    trigger={
                      <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                        Generate makeup diagnostic classes
                      </DropdownMenuItem>
                    }
                    onCoachesAssigned={({
                      availableCoachesCount,
                      coachesAssignedCount,
                      reservedFallbackGroupCount,
                    }) => {
                      setCoachesAssignedCount({
                        availableCoachesCount,
                        coachesAssignedCount,
                        reservedFallbackGroupCount,
                      });
                      setShowCoachWarningDialog(true);
                    }}
                  />
                  <GenerateRegularSessions
                    students={rowDataSelection}
                    orgId={school.org.id}
                    onSuccess={onSuccess}
                    trigger={
                      <DropdownMenuItem onSelect={(e) => e.preventDefault()}>Generate regular classes</DropdownMenuItem>
                    }
                    onCoachesAssigned={({
                      availableCoachesCount,
                      coachesAssignedCount,
                      reservedFallbackGroupCount,
                    }) => {
                      setCoachesAssignedCount({
                        availableCoachesCount,
                        coachesAssignedCount,
                        reservedFallbackGroupCount,
                      });
                      setShowCoachWarningDialog(true);
                    }}
                    schoolTimeZone={school.org.timeZone}
                  />
                </DropdownMenuSubContent>
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    const selectedRows = table.getSelectedRowModel().rows.map((r) => r.original);
                    const date = getNowPlainDateISO(actingUser.timeZone).toString();
                    exportToCSV(selectedRows, exportColumns, `students_short_links_${date}.csv`);
                  }}
                >
                  Export short link as CSV
                </DropdownMenuItem>
              </DropdownMenuSub>
              <DropdownMenuItem
                onSelect={async (e) => {
                  e.preventDefault();
                  try {
                    const selectedRows = table.getSelectedRowModel().rows.map((r) => r.original);
                    await generateQrCodesPdf(selectedRows, actingUser.timeZone);
                    toast({
                      title: 'QR codes PDF generated successfully',
                    });
                  } catch (error) {
                    console.error('Failed to generate QR codes PDF:', error);
                    toast({
                      title: 'Failed to generate QR codes PDF',
                      description: error instanceof Error ? error.message : 'An error occurred',
                      variant: 'destructive',
                    });
                  }
                }}
              >
                Export QR code as PDF
              </DropdownMenuItem>
            </>
          }
        />
      );
    }

    // Return null for loading state without active filters
    return null;
  };

  return (
    <>
      <InfoPage
        headerText={<SchoolPageTitle name={school.name || school.id} isHighPriority={school.isHighPriority} />}
        breadcrumbs={{
          linkItems: [
            {
              label: 'Schools',
              link: {
                to: '/orgs/schools',
              },
            },
          ],
          currentPage: school.name,
        }}
        tabs={schoolDetailsTabs({ schoolId })}
      >
        {renderStudentsTable()}
      </InfoPage>
      {showCoachWarningDialog && (
        <DialogCoachesAssigned
          availableCoachesCount={coachesAssignedCount.availableCoachesCount}
          coachesAssignedCount={coachesAssignedCount.coachesAssignedCount}
          reservedFallbackGroupCount={coachesAssignedCount.reservedFallbackGroupCount}
          open={showCoachWarningDialog}
          setOpen={setShowCoachWarningDialog}
        />
      )}
    </>
  );
}
